// src/App.tsx - Updated with Enter Name as first page
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import apiClient from './config/api';

// Import your existing pages
import LoginPage from './pages/LoginPage';
import EnterNamePage from './pages/EnterNamePage'; // NEW PAGE
import HistoryPage from './pages/HistoryPage';
import ScanTagPage from './pages/ScanTagPage';
import ScanUpcPage from './pages/ScanUpcPage';
import UpdateCountPage from './pages/UpdateCountPage';

import logo from './logo.svg';
import './App.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

interface ApiStatus {
  status: 'loading' | 'connected' | 'error';
  message: string;
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Session Protected Route - requires both auth and name entry
const SessionProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.log('SessionProtectedRoute - isAuthenticated:', isAuthenticated); // Debug log
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Dynamic Home Route Component - Updated to go to enter-name first
const DynamicHome: React.FC = () => {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const [nameChecked, setNameChecked] = useState(false);
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Check name after auth is confirmed
    if (isAuthenticated && !isLoading) {
      setNameChecked(true);
    }
  }, [isAuthenticated, isLoading]);
  
  if (isLoading || !nameChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Check if user has entered name for this session
    const userName = localStorage.getItem('scannerUserName');
    console.log('DynamicHome - userName from localStorage:', userName); // Debug log
    
    if (userName && userName.trim() !== '') {
      return <Navigate to="/scan-tag" replace />;
    } else {
      return <Navigate to="/enter-name" replace />;
    }
  } else {
    return <Navigate to="/login" replace />;
  }
};

// Home Component - Updated with session management
const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [apiStatus, setApiStatus] = useState<ApiStatus>({
    status: 'loading',
    message: 'Checking API connection...'
  });
  const [data, setData] = useState<any>(null);
  
  // Get current user name from session
  const userName = localStorage.getItem('scannerUserName');
  const sessionStart = localStorage.getItem('scannerSessionStart');

  useEffect(() => {
    const testApiConnection = async () => {
      try {
        setApiStatus({ status: 'loading', message: 'Testing API connection...' });
        const response = await apiClient.get('/health');
        setApiStatus({
          status: 'connected',
          message: `Connected to API at ${apiClient.defaults.baseURL}`
        });
        setData(response.data);
      } catch (error: any) {
        console.error('API connection failed:', error);
        setApiStatus({
          status: 'error',
          message: error.message || 'Failed to connect to API'
        });
      }
    };

    testApiConnection();
  }, []);

  const handleTestRequest = async () => {
    try {
      setApiStatus({ status: 'loading', message: 'Making test request...' });
      const response = await apiClient.get('/test');
      setData(response.data);
      setApiStatus({
        status: 'connected',
        message: 'Test request successful!'
      });
    } catch (error: any) {
      console.error('Test request failed:', error);
      setApiStatus({
        status: 'error',
        message: error.message || 'Test request failed'
      });
    }
  };

  const getStatusColor = () => {
    switch (apiStatus.status) {
      case 'loading': return '#fbbf24';
      case 'connected': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Clear session data on logout
      localStorage.removeItem('scannerBatchId');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNewSession = () => {
    // Clear current session and start new one
    localStorage.removeItem('scannerBatchId');
    navigate('/enter-name');
  };

  return (
    <div className="App">
      <header className="App-header">
        <img src={logo} className="App-logo" alt="logo" />
        
        {isAuthenticated && userName && (
          <div style={{
            padding: '8px 16px',
            marginBottom: '10px',
            backgroundColor: '#4f46e5',
            borderRadius: '6px',
            color: 'white',
            fontSize: '14px'
          }}>
            Welcome, {userName}! Scanner Session Active
          </div>
        )}
        
        <div style={{
          padding: '12px 20px',
          margin: '20px 0',
          backgroundColor: getStatusColor(),
          borderRadius: '8px',
          color: 'white',
          fontWeight: 'bold'
        }}>
          API Status: {apiStatus.message}
        </div>

        <p>Edit <code>src/App.tsx</code> and save to reload.</p>

        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          flexWrap: 'wrap', 
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <button
            onClick={handleTestRequest}
            disabled={apiStatus.status === 'loading'}
            style={{
              padding: '10px 20px',
              backgroundColor: '#61dafb',
              border: 'none',
              borderRadius: '5px',
              color: '#282c34',
              fontWeight: 'bold',
              cursor: apiStatus.status === 'loading' ? 'not-allowed' : 'pointer',
              opacity: apiStatus.status === 'loading' ? 0.6 : 1
            }}
          >
            {apiStatus.status === 'loading' ? 'Loading...' : 'Test API'}
          </button>

          {!isAuthenticated ? (
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4f46e5',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Login
            </button>
          ) : (
            <>
              {userName ? (
                <>
                  <button
                    onClick={() => navigate('/scan-tag')}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#059669',
                      border: 'none',
                      borderRadius: '5px',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Continue Scanning
                  </button>
                  
                  <button
                    onClick={handleNewSession}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc2626',
                      border: 'none',
                      borderRadius: '5px',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    New Session
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/enter-name')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#059669',
                    border: 'none',
                    borderRadius: '5px',
                    color: 'white',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Start Scanning
                </button>
              )}
              
              <button
                onClick={() => navigate('/history')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#7c3aed',
                  border: 'none',
                  borderRadius: '5px',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                History
              </button>

              <button
                onClick={handleLogout}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '5px',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Logout
              </button>
            </>
          )}
        </div>

        {data && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            color: '#1f2937',
            maxWidth: '600px',
            textAlign: 'left'
          }}>
            <strong>API Response:</strong>
            <pre style={{ 
              fontSize: '12px', 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}

        <a
          className="App-link"
          href="https://reactjs.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn React
        </a>
      </header>
    </div>
  );
};

// Router Component - Updated with new flow
const AppRouter: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<DynamicHome />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/home" element={<Home />} />
      
      {/* Enter Name Route - Protected by auth only */}
      <Route 
        path="/enter-name" 
        element={
          <ProtectedRoute>
            <EnterNamePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Scanning Routes - Protected by both auth and name entry */}
      <Route 
        path="/scan-tag" 
        element={
          <SessionProtectedRoute>
            <ScanTagPage />
          </SessionProtectedRoute>
        } 
      />
      <Route 
        path="/scan-upc/:tagCode" 
        element={
          <SessionProtectedRoute>
            <ScanUpcPage />
          </SessionProtectedRoute>
        } 
      />
      <Route 
        path="/update-count/:tagCode/:upc" 
        element={
          <SessionProtectedRoute>
            <UpdateCountPage />
          </SessionProtectedRoute>
        } 
      />
      
      {/* History can be accessed without active session */}
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        } 
      />
      
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Main App Component with all providers
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRouter />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;